import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, LogOut, Menu, Search, Settings, Sparkles, User } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/ui/Avatar';
import { BackLink } from '@/components/ui/BackLink';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { GlobalSearch } from './GlobalSearch';
import { NewMenu } from './NewMenu';
import { HelpButton } from './HelpButton';
import { breadcrumbsFor, visibleNavItems } from '@/nav/navigation';
import type { AuthUser } from '@/types';

export function Topbar({
  user,
  onOpenMobileNav,
  onSignOut,
}: {
  user: AuthUser;
  onOpenMobileNav: () => void;
  onSignOut: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const crumbs = breadcrumbsFor(location.pathname);
  // Insights and the company-wide Finance ledgers sit here, beside settings (round 6).
  const profileItems = visibleNavItems({
    feedback: Boolean(user.feedback_access),
    executive: Boolean(user.executive_access),
    finance: Boolean(user.finance_access),
  }).filter((item) => item.placement === 'profile');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Collapse the menu whenever the route changes.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <header className="relative flex h-topbar items-center gap-3 border-b border-line bg-white px-4 sm:px-6">
      {/* The selected project's colour, across the top of every screen. */}
      <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-accent transition-colors" />
      <button
        type="button"
        onClick={onOpenMobileNav}
        aria-label="Open navigation"
        className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-cloud hover:text-body lg:hidden"
      >
        <Menu aria-hidden className="h-5 w-5" />
      </button>

      <BackLink className="-ml-1" />

      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        <ol className="flex items-center gap-2 truncate text-sm">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <Fragment key={`${crumb.label}-${index}`}>
                {index > 0 && (
                  <li aria-hidden className="shrink-0 text-muted/60">
                    /
                  </li>
                )}
                <li className={cn('truncate', isLast ? 'font-semibold text-ink' : 'text-muted')}>
                  {crumb.path && !isLast ? (
                    <Link to={crumb.path} className="transition-colors hover:text-body">
                      {crumb.label}
                    </Link>
                  ) : (
                    crumb.label
                  )}
                </li>
              </Fragment>
            );
          })}
        </ol>
      </nav>

      <div className="relative hidden w-full max-w-[360px] shrink-0 md:block">
        <GlobalSearch />
      </div>

      <button
        type="button"
        aria-label="Search"
        onClick={() => setSearchOpen(true)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-cloud hover:text-body md:hidden"
      >
        <Search aria-hidden className="h-[18px] w-[18px]" />
      </button>
      {searchOpen && (
        // On a phone the search takes the top of the screen, where the thumb can reach the results.
        <div className="fixed inset-0 z-50 bg-midnight/40 md:hidden" onClick={() => setSearchOpen(false)}>
          <div className="bg-white p-3 shadow-flyout" onClick={(event) => event.stopPropagation()}>
            <GlobalSearch autoFocus onNavigate={() => setSearchOpen(false)} />
          </div>
        </div>
      )}

      <Link
        to="/agent"
        aria-label="OneParacon Agent"
        title="Ask OneParacon Agent"
        className={cn(
          'flex h-10 shrink-0 items-center gap-2 rounded-full px-3 text-sm font-semibold transition-colors',
          location.pathname.startsWith('/agent') ? 'bg-violet-100 text-violet-600' : 'bg-cloud text-body hover:bg-violet-50 hover:text-violet-600',
        )}
      >
        <Sparkles aria-hidden className="h-[18px] w-[18px]" />
        <span className="hidden xl:inline">Agent</span>
      </Link>

      {user.role !== 'worker' && <HelpButton />}

      <NewMenu user={user} />

      {/* A worker login has no staff notifications. */}
      {user.role !== 'worker' && <NotificationBell />}

      <div ref={menuRef} className="relative shrink-0">
        <button
          type="button"
          onClick={() => setMenuOpen((value) => !value)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-cloud"
        >
          <Avatar name={user.full_name} src={user.avatar_url} size="md" />
          <span className="hidden max-w-[160px] truncate text-sm font-semibold text-body lg:block">
            {user.full_name}
          </span>
          <ChevronDown aria-hidden className="hidden h-4 w-4 text-muted lg:block" />
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full z-30 mt-2 w-60 animate-fade-in overflow-hidden rounded-xl border border-line bg-white p-1.5 shadow-raised"
          >
            <div className="border-b border-line px-3 pb-2.5 pt-2">
              <p className="truncate text-sm font-semibold text-body">{user.full_name}</p>
              <p className="truncate text-helper text-muted">{user.email}</p>
              <p className="mt-1 truncate text-2xs text-muted">
                {user.role_label} · {user.company_name}
              </p>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => navigate('/profile')}
              className="mt-1 flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm text-body transition-colors hover:bg-cloud"
            >
              <User aria-hidden className="h-4 w-4 text-muted" />
              My Profile
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => navigate('/profile/notifications')}
              className="flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm text-body transition-colors hover:bg-cloud"
            >
              <Settings aria-hidden className="h-4 w-4 text-muted" />
              Settings
            </button>
            {/*
              Company settings live here rather than in the sidebar (client
              feedback round 2, #28): it is configuration, not a place you go
              to do the day job, and it was crowding the menu.
            */}
            <button
              type="button"
              role="menuitem"
              onClick={() => navigate('/company')}
              className="flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm text-body transition-colors hover:bg-cloud"
            >
              <Building2 aria-hidden className="h-4 w-4 text-muted" />
              Company settings
            </button>
            {profileItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => navigate(item.path)}
                  className="flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm text-body transition-colors hover:bg-cloud"
                >
                  <Icon aria-hidden className="h-4 w-4 text-muted" />
                  {item.label}
                </button>
              );
            })}
            <button
              type="button"
              role="menuitem"
              onClick={onSignOut}
              className="flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm text-danger-600 transition-colors hover:bg-danger-50"
            >
              <LogOut aria-hidden className="h-4 w-4" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
